import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PLATFORMS, useRentals, RentalListing } from "@/hooks/use-rentals";
import { optionalEmailError, toPersistedEmail } from "@/lib/validation";
import { cn } from "@/lib/utils";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Active listings only — rented/draft/paused/archived listings are excluded upstream. */
  listings: RentalListing[];
}

export function AddLeadDialog({ open, onOpenChange, listings }: Props) {
  const { createLead } = useRentals();
  const [listingId, setListingId] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [platform, setPlatform] = useState("");
  const [message, setMessage] = useState("");

  // Email is optional, but a typed-in value must be a valid address (zod).
  // Shown live under the field and blocks submit.
  const emailError = optionalEmailError(email);

  const handleSubmit = () => {
    if (!listingId || !name || emailError) return;
    createLead.mutate(
      {
        listing_id: listingId,
        name,
        email: toPersistedEmail(email),
        phone: phone || null,
        platform: platform || null,
        message: message || null,
        status: "new",
        showing_date: null,
        first_contacted_at: null,
        notes: null,
      },
      {
        onSuccess: () => {
          onOpenChange(false);
          setListingId("");
          setName("");
          setEmail("");
          setPhone("");
          setPlatform("");
          setMessage("");
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Lead</DialogTitle>
          <DialogDescription>
            Track an inquiry from a prospective tenant
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Listing *</Label>
            <Select value={listingId} onValueChange={setListingId}>
              <SelectTrigger>
                <SelectValue placeholder="Select listing" />
              </SelectTrigger>
              <SelectContent>
                {listings.map((l) => (
                  <SelectItem key={l.id} value={l.id}>
                    {l.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="lead-email">Email</Label>
              <Input
                id="lead-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tenant@email.com"
                aria-invalid={!!emailError}
                aria-describedby={emailError ? "lead-email-error" : undefined}
                className={cn(
                  emailError &&
                    "border-destructive focus-visible:ring-destructive",
                )}
              />
              {emailError && (
                <p
                  id="lead-email-error"
                  className="text-xs text-destructive mt-1"
                >
                  {emailError}
                </p>
              )}
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
          </div>
          <div>
            <Label>Source Platform</Label>
            <Select value={platform} onValueChange={setPlatform}>
              <SelectTrigger>
                <SelectValue placeholder="Where did they find it?" />
              </SelectTrigger>
              <SelectContent>
                {PLATFORMS.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Message</Label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              !listingId || !name || !!emailError || createLead.isPending
            }
          >
            {createLead.isPending ? "Adding..." : "Add Lead"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
